// ─────────────────────────────────────────────────────────────────────────
// postcode.ts — lookup over the built postcode→zone table.
//
// The table itself (data/postcodeZones.json) is LAZY-FETCHED, never inlined in
// the bundle. `loadPostcodeTable` fetches once and caches the parsed result in
// memory; `lookupPostcode` is a pure lookup over an already-loaded table.
// ─────────────────────────────────────────────────────────────────────────

import type { PostcodeZone } from './types.js';

export interface PostcodeTableMeta {
  version: string;
  generatedAt: string;
  count: number;
  sources: Array<{
    name: string;
    used: string;
    license: string;
    url: string;
    attribution: string;
  }>;
}

export interface PostcodeTable {
  meta: PostcodeTableMeta;
  zones: Record<string, PostcodeZone>;
}

/** Normalise user input to a 4-digit AU postcode string, or null if invalid. */
export function normalizePostcode(input: string): string | null {
  const digits = (input ?? '').trim().replace(/\D/g, '');
  if (digits.length !== 4) return null;
  return digits;
}

/** Pure lookup over a loaded table. */
export function lookupPostcode(
  table: PostcodeTable,
  postcode: string,
): PostcodeZone | null {
  const pc = normalizePostcode(postcode);
  if (!pc) return null;
  return table.zones[pc] ?? null;
}

// In-memory cache keyed by URL so repeated mounts share one fetch.
const cache = new Map<string, Promise<PostcodeTable>>();

type FetchLike = (url: string) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

/**
 * Lazy-fetch and cache the postcode table. Pass a custom `fetchImpl` for tests
 * or non-browser hosts; defaults to global `fetch`.
 */
export function loadPostcodeTable(
  url: string,
  fetchImpl?: FetchLike,
): Promise<PostcodeTable> {
  const existing = cache.get(url);
  if (existing) return existing;

  const f: FetchLike =
    fetchImpl ?? ((u: string) => (globalThis.fetch as unknown as FetchLike)(u));

  const promise = f(url)
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(`Postcode table fetch failed: HTTP ${res.status}`);
      }
      return (await res.json()) as PostcodeTable;
    })
    .catch((err) => {
      cache.delete(url); // allow a retry on transient failure
      throw err;
    });

  cache.set(url, promise);
  return promise;
}

/** Clear the in-memory cache (mainly for tests). */
export function clearPostcodeCache(): void {
  cache.clear();
}
